import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  InputAdornment, 
  Button, 
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Grid,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Divider
} from '@mui/material';
import { DataGrid, GridColDef, GridValueGetterParams, GridRenderCellParams, GridPaginationModel, GridValueFormatterParams } from '@mui/x-data-grid';
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../shared/utils/firebase';
import { format } from 'date-fns';
import { LoadingSpinner } from '../../shared/components';
import { useClientDirectory } from './hooks/useClientDirectory';
import { ClientDirectoryClient } from './services/clientDirectoryService';

const ClientOverview: React.FC = () => {
  const { user, userRole, userMetadata } = useAuth();
  // --- Audit log for overview page access (throttled) ---
  useEffect(() => {
    const logClientOverviewAccess = async () => {
      const auditUserId = user?.uid || (userMetadata && 'uid' in userMetadata ? userMetadata.uid : undefined);
      const auditUserName = userMetadata && 'displayName' in userMetadata ? userMetadata.displayName : undefined;
      const auditUserEmail = userMetadata && 'email' in userMetadata ? userMetadata.email : undefined;
      if (!auditUserId) return;
      const logKey = `client_overview_log_last_${auditUserId}`;
      const lastLog = localStorage.getItem(logKey);
      const now = Date.now();
      if (lastLog && now - parseInt(lastLog, 10) < 3 * 60 * 60 * 1000) return;
      try {
        await setDoc(doc(db, 'audit_logs', `client_overview_${now}`), {
          action: 'Open Client Overview',
          actionType: 'ACCESS',
          userId: auditUserId,
          userName: auditUserName || auditUserEmail || 'Unknown',
          userRole: userRole || 'Unknown',
          timestamp: serverTimestamp(),
          details: 'Client Overview Accessed'
        });
        localStorage.setItem(logKey, now.toString());
      } catch (error) {
        console.warn('Failed to log Client Overview access', error);
      }
    };
    logClientOverviewAccess();
  }, [user, userMetadata, userRole]);
  const navigate = useNavigate();
  const { clients: directoryClients, loading, error, refresh } = useClientDirectory({ enabled: true });
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClient, setSelectedClient] = useState<ClientDirectoryClient | null>(null);
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [coachFilter, setCoachFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [reportStatusFilter, setReportStatusFilter] = useState<string>('');
  const [availableCoaches, setAvailableCoaches] = useState<{id: string, name: string}[]>([]);
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);

  const filterStorageKey = useMemo(() => {
    if (!user?.uid) return null;
    return `client_overview_filters_${user.uid}`;
  }, [user?.uid]);

  const filtersLoadedRef = useRef(false);

  useEffect(() => {
    if (!filterStorageKey || filtersLoadedRef.current) return;
    try {
      const stored = localStorage.getItem(filterStorageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed.searchTerm === 'string') setSearchTerm(parsed.searchTerm);
        setActiveFilter(parsed.activeFilter ?? null);
        if (typeof parsed.coachFilter === 'string') setCoachFilter(parsed.coachFilter);
        if (typeof parsed.locationFilter === 'string') setLocationFilter(parsed.locationFilter);
        if (typeof parsed.reportStatusFilter === 'string') setReportStatusFilter(parsed.reportStatusFilter);
      }
    } catch (error) {
      console.warn('Failed to load stored client filters', error);
    } finally {
      filtersLoadedRef.current = true;
    }
  }, [filterStorageKey]);

  useEffect(() => {
    if (!filterStorageKey || !filtersLoadedRef.current) return;
    try {
      const payload = {
        searchTerm,
        activeFilter,
        coachFilter,
        locationFilter,
        reportStatusFilter,
      };
      localStorage.setItem(filterStorageKey, JSON.stringify(payload));
    } catch (error) {
      console.warn('Failed to persist client filters', error);
    }
  }, [filterStorageKey, searchTerm, activeFilter, coachFilter, locationFilter, reportStatusFilter]);

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      searchTerm ||
      activeFilter ||
      coachFilter ||
      locationFilter ||
      reportStatusFilter
    );
  }, [searchTerm, activeFilter, coachFilter, locationFilter, reportStatusFilter]);

  const clearAllFilters = useCallback(() => {
    setSearchTerm('');
    setActiveFilter(null);
    setCoachFilter('');
    setLocationFilter('');
    setReportStatusFilter('');
  }, []);
  
  // Handle pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  
  // Add pagination state for lead coach and collaborator clients
  const [leadCoachPage, setLeadCoachPage] = useState(0);
  const [leadCoachPageSize, setLeadCoachPageSize] = useState(10);
  const [collaboratorPage, setCollaboratorPage] = useState(0);
  const [collaboratorPageSize, setCollaboratorPageSize] = useState(10);

  useEffect(() => {
    setPage(0);
    setLeadCoachPage(0);
    setCollaboratorPage(0);
  }, [searchTerm, activeFilter, coachFilter, locationFilter, reportStatusFilter]);

  // Function to transform name from "Lastname, Firstname" to "Firstname Lastname"
  const transformName = (name: string | null | undefined): string => {
    if (!name) return 'Unknown';
    
    // Check if the name is in "Lastname, Firstname" format
    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(part => part.trim());
      return `${firstName} ${lastName}`;
    }
    
    return name;
  };

  // Function to get report status display
  const getReportStatusDisplay = (client: ClientDirectoryClient): { status: string; color: 'success' | 'warning' | 'error' | 'default' } => {
    if (client.report_status) {
      return { status: client.report_status, color: 'success' };
    }
    
    // Check if initial assessment reports are submitted
    const initialReportsSubmitted = 
      client.assessment_inbody_data_submitted &&
      client.assessment_nutrition_data_submitted &&
      client.assessment_metabolic_data_submitted &&
      client.assessment_movement_data_submitted &&
      client.assessment_training_data_submitted;
      
    // Check if pre-assessment data is submitted
    const preAssessmentSubmitted = 
      client.pre_health_consult_data_submitted &&
      client.health_consult_data_submitted &&
      client.client_intake_data_submitted;
      
    if (initialReportsSubmitted && preAssessmentSubmitted) {
      return { status: 'Ready for Report', color: 'warning' };
    } else if (preAssessmentSubmitted) {
      return { status: 'Assessments Pending', color: 'warning' };
    } else {
      return { status: 'Incomplete', color: 'error' };
    }
  };

  // Function to filter clients based on search term and filters
  const filterClients = useCallback((clientsList: ClientDirectoryClient[]): ClientDirectoryClient[] => {
    return clientsList.filter(client => {
      // Search term filter
      const matchesSearch = searchTerm === '' || 
        (client.client_name && transformName(client.client_name).toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.client_id && client.client_id.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.email && client.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (client.lead_coach_name && transformName(client.lead_coach_name).toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Active/inactive filter
      const matchesActiveFilter = 
        activeFilter === null || 
        (activeFilter === 'active' && client.active_programme) ||
        (activeFilter === 'inactive' && !client.active_programme);
      
      // Coach filter
      const matchesCoachFilter = 
        coachFilter === '' || 
        client.lead_coach_id === coachFilter;
      
      // Location filter
      const matchesLocationFilter = 
        locationFilter === '' || 
        (client.primary_location && client.primary_location.includes(locationFilter));
      
      // Report status filter
      const reportStatus = getReportStatusDisplay(client);
      const matchesReportStatusFilter = 
        reportStatusFilter === '' || 
        reportStatus.status.toLowerCase().includes(reportStatusFilter.toLowerCase());
      
      return matchesSearch && matchesActiveFilter && matchesCoachFilter && matchesLocationFilter && matchesReportStatusFilter;
    });
  }, [searchTerm, activeFilter, coachFilter, locationFilter, reportStatusFilter]);

  const coachId = useMemo(() => {
    return userMetadata?.coach_id != null ? String(userMetadata.coach_id) : null;
  }, [userMetadata?.coach_id]);

  const leadCoachClients = useMemo(() => {
    if (userRole !== 'USER' || !coachId) return [];
    return directoryClients.filter(client => client.lead_coach_id === coachId);
  }, [coachId, directoryClients, userRole]);

  const collaboratorClients = useMemo(() => {
    if (userRole !== 'USER' || !coachId) return [];
    return directoryClients.filter(
      client =>
        client.lead_coach_id !== coachId &&
        Array.isArray(client.collaborating_coach_ids) &&
        client.collaborating_coach_ids.includes(coachId)
    );
  }, [coachId, directoryClients, userRole]);

  const filteredLeadCoachClients = useMemo(() => {
    return filterClients(leadCoachClients);
  }, [filterClients, leadCoachClients]);

  const filteredCollaboratorClients = useMemo(() => {
    return filterClients(collaboratorClients);
  }, [filterClients, collaboratorClients]);

  const filteredAdminManagerClients = useMemo(() => {
    return filterClients(directoryClients);
  }, [directoryClients, filterClients]);

  useEffect(() => {
    const coaches = new Set<string>();
    const coachNameById = new Map<string, string>();
    const locations = new Set<string>();

    directoryClients.forEach(client => {
      if (client.lead_coach_id) {
        coaches.add(client.lead_coach_id);
        if (client.lead_coach_name && !coachNameById.has(client.lead_coach_id)) {
          coachNameById.set(client.lead_coach_id, transformName(client.lead_coach_name));
        }
      }
      if (client.primary_location) {
        locations.add(client.primary_location);
      }
    });

    const coachesArray = Array.from(coaches)
      .map(coachId => ({ id: coachId, name: coachNameById.get(coachId) || 'Unknown Coach' }))
      .sort((a, b) => a.name.localeCompare(b.name));

    setAvailableCoaches(coachesArray);
    setAvailableLocations(Array.from(locations).sort((a, b) => a.localeCompare(b)));
  }, [directoryClients]);

  const handleRowClick = (params: any) => {
    navigate(`/clients/${params.id}`);
  };

  const handleActionClick = (event: React.MouseEvent<HTMLButtonElement>, client: ClientDirectoryClient) => {
    setAnchorEl(event.currentTarget);
    setSelectedClient(client);
  };

  const handleActionClose = () => {
    setAnchorEl(null);
    setSelectedClient(null);
  };

  const handleViewClient = () => {
    if (selectedClient) {
      navigate(`/clients/${selectedClient.id}`);
    }
    handleActionClose();
  };

  const handleEditClient = () => {
    if (selectedClient) {
      navigate(`/clients/${selectedClient.id}/edit`);
    }
    handleActionClose();
  };

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterMenuAnchorEl(null);
  };

  const applyFilter = (filter: string | null) => {
    setActiveFilter(filter);
    handleFilterClose();
  };

  const handleCoachFilterChange = (event: SelectChangeEvent) => {
    setCoachFilter(event.target.value);
  };

  const handleLocationFilterChange = (event: SelectChangeEvent) => {
    setLocationFilter(event.target.value);
  };

  const handleReportStatusFilterChange = (event: SelectChangeEvent) => {
    setReportStatusFilter(event.target.value);
  };

  const columns: GridColDef[] = [
    { 
      field: 'client_id', 
      headerName: 'Client ID', 
      width: 120 
    },
    { 
      field: 'client_name', 
      headerName: 'Name', 
      width: 200,
      valueGetter: (params: GridValueGetterParams) => transformName(params.row.client_name)
    },
    { 
      field: 'first_session_ts', 
      headerName: 'First Visit', 
      width: 140,
      valueGetter: (params: GridValueGetterParams) => params.row.first_session_ts,
      valueFormatter: (params: GridValueFormatterParams<number | null>) => {
        if (!params.value) return 'N/A';
        return format(new Date(params.value), 'dd MMM yyyy');
      }
    },
    { 
      field: 'last_visit', 
      headerName: 'Last Visit', 
      width: 140,
      valueGetter: (params: GridValueGetterParams) => params.row.last_visit_ts,
      valueFormatter: (params: GridValueFormatterParams<number | null>) => {
        if (!params.value) return 'No visits';
        return format(new Date(params.value), 'dd MMM yyyy');
      }
    },
    { 
      field: 'lead_coach_name', 
      headerName: 'Coach', 
      width: 150,
      valueGetter: (params: GridValueGetterParams) => transformName(params.row.lead_coach_name)
    },
    { 
      field: 'primary_location', 
      headerName: 'Location', 
      width: 150,
      valueGetter: (params: GridValueGetterParams) => params.row.primary_location || 'N/A'
    },
    { 
      field: 'report_status', 
      headerName: 'Report Status', 
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const status = getReportStatusDisplay(params.row);
        return (
          <Chip 
            label={status.status} 
            color={status.color}
            size="small"
          />
        );
      }
    },
    { 
      field: 'active_programme', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.row.active_programme ? 'Active' : 'Inactive'} 
          color={params.row.active_programme ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'conversion_status', 
      headerName: 'Conversion', 
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.row.conversion_status ? 'Converted' : 'Not Converted'} 
          color={params.row.conversion_status ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { 
      field: 'actions', 
      headerName: 'Actions', 
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton 
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            handleActionClick(event, params.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )
    },
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Clients
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search clients by name, ID, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              gap: 1,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={handleFilterClick}
              sx={{ minWidth: 140 }}
            >
              {activeFilter ? `Filter: ${activeFilter}` : 'Status Filter'}
            </Button>
            {hasActiveFilters && (
              <Button
                variant="text"
                onClick={clearAllFilters}
                sx={{ textTransform: 'none' }}
              >
                Clear Filters
              </Button>
            )}
          </Grid>
        </Grid>
        
        {/* Advanced Filters for Admin and Manager */}
        {(userRole === 'ADMIN' || userRole === 'MANAGER') && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="coach-filter-label">Filter by Coach</InputLabel>
                <Select
                  labelId="coach-filter-label"
                  value={coachFilter}
                  onChange={handleCoachFilterChange}
                  label="Filter by Coach"
                >
                  <MenuItem value="">All Coaches</MenuItem>
                  {availableCoaches.map((coach) => (
                    <MenuItem key={coach.id} value={coach.id}>{coach.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="location-filter-label">Filter by Location</InputLabel>
                <Select
                  labelId="location-filter-label"
                  value={locationFilter}
                  onChange={handleLocationFilterChange}
                  label="Filter by Location"
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {availableLocations.map((location) => (
                    <MenuItem key={location} value={location}>{location}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="report-status-filter-label">Report Status</InputLabel>
                <Select
                  labelId="report-status-filter-label"
                  value={reportStatusFilter}
                  onChange={handleReportStatusFilterChange}
                  label="Report Status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Ready">Ready for Report</MenuItem>
                  <MenuItem value="Incomplete">Incomplete</MenuItem>
                  <MenuItem value="Assessments">Assessments Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}{' '}
            <Button variant="text" size="small" onClick={refresh} sx={{ ml: 1, textTransform: 'none' }}>
              Retry
            </Button>
          </Alert>
        )}
        
        {/* Display Lead Coach Clients */}
        {userRole === 'USER' && userMetadata?.coach_id && (
          <>
	            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
	              Your Clients
	            </Typography>
	            <div style={{ height: 400, width: '100%', marginBottom: 4 }}>
	              {loading ? (
	                <LoadingSpinner minHeight="200px" />
	              ) : (
	                <DataGrid
	                  rows={filteredLeadCoachClients}
	                  columns={columns}
	                  pagination
	                  pageSizeOptions={[5, 10, 25]}
	                  paginationModel={{ page: leadCoachPage, pageSize: leadCoachPageSize }}
	                  onPaginationModelChange={(model: GridPaginationModel) => {
                    setLeadCoachPage(model.page);
                    setLeadCoachPageSize(model.pageSize);
                  }}
                  onRowClick={handleRowClick}
                  disableRowSelectionOnClick
                  disableColumnSelector
                  disableDensitySelector
                  loading={loading}
                  sx={{
                    '& .MuiDataGrid-cell:hover': {
                      cursor: 'pointer',
                    },
                  }}
                />
              )}
            </div>
            
            {/* Display Collaborator Clients */}
            {collaboratorClients.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Collaborator Clients
                </Typography>
	                <div style={{ height: 400, width: '100%' }}>
	                  <DataGrid
	                    rows={filteredCollaboratorClients}
	                    columns={columns}
	                    pagination
	                    pageSizeOptions={[5, 10, 25]}
	                    paginationModel={{ page: collaboratorPage, pageSize: collaboratorPageSize }}
                    onPaginationModelChange={(model: GridPaginationModel) => {
                      setCollaboratorPage(model.page);
                      setCollaboratorPageSize(model.pageSize);
                    }}
                    onRowClick={handleRowClick}
                    disableRowSelectionOnClick
                    disableColumnSelector
                    disableDensitySelector
                    loading={loading}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        cursor: 'pointer',
                      },
                    }}
                  />
                </div>
              </>
            )}
          </>
        )}
        
        {/* Display All Clients for Admin/Manager */}
	        {(userRole === 'ADMIN' || userRole === 'MANAGER') && (
	          <div style={{ height: 600, width: '100%' }}>
	            {loading ? (
	              <LoadingSpinner />
	            ) : (
	              <DataGrid
	                rows={filteredAdminManagerClients}
	                columns={columns}
	                pagination
	                pageSizeOptions={[10, 25, 50]}
	                paginationModel={{ page, pageSize }}
                onPaginationModelChange={(model: GridPaginationModel) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                }}
	                rowCount={filteredAdminManagerClients.length}
	                onRowClick={handleRowClick}
	                disableRowSelectionOnClick
	                disableColumnSelector
	                disableDensitySelector
                loading={loading}
                sx={{
                  '& .MuiDataGrid-cell:hover': {
                    cursor: 'pointer',
                  },
                }}
              />
            )}
          </div>
        )}
      </Paper>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={handleViewClient}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">View Details</Typography>
        </MenuItem>
        {(userRole === 'ADMIN' || userRole === 'MANAGER' || 
         (userRole === 'USER' && selectedClient?.lead_coach_id === userMetadata?.coach_id)) && (
          <MenuItem onClick={handleEditClient}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Edit</Typography>
          </MenuItem>
        )}
      </Menu>
      
      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchorEl}
        open={Boolean(filterMenuAnchorEl)}
        onClose={handleFilterClose}
      >
        <MenuItem onClick={() => applyFilter(null)}>
          <Typography variant="inherit">All Clients</Typography>
        </MenuItem>
        <MenuItem onClick={() => applyFilter('active')}>
          <Typography variant="inherit">Active Clients</Typography>
        </MenuItem>
        <MenuItem onClick={() => applyFilter('inactive')}>
          <Typography variant="inherit">Inactive Clients</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ClientOverview;
